import { WorkspacePlan } from '@up2stay/up2promo-api-client';

export const FEATURE_KEYS = [
  'feature.invite_members',
  'feature.custom_roles',
  'feature.integrations',
  'feature.reputation',
  'feature.reputation_ai',
  'feature.advanced_analytics',
  'feature.competitor_benchmark',
  'feature.lead_events',
  'feature.feedback',
  'feature.profile_branding',
  'feature.sso',
] as const;

export type FeatureKey = (typeof FEATURE_KEYS)[number];

export const QUOTA_KEYS = [
  'quota.included_reputation_locations',
  'quota.members',
  'quota.feedback_invite_recipients',
  'quota.analytics_history_days',
] as const;

export type QuotaKey = (typeof QUOTA_KEYS)[number];

/** Sentinel value from the API meaning no quota cap. */
export const UNLIMITED_QUOTA = -1;

export function isUnlimitedQuota(
  limit: number | undefined | null,
): limit is typeof UNLIMITED_QUOTA {
  return limit === UNLIMITED_QUOTA;
}

/** Monthly list price for one extra reputation location (EUR). */
export const REPUTATION_LOCATION_ADDON_MONTHLY_EUR = 15;

export const FEATURE_METADATA: Record<
  FeatureKey,
  { labelKey: string; descriptionKey?: string }
> = {
  'feature.invite_members': { labelKey: 'entitlements.feature.invite_members' },
  'feature.custom_roles': { labelKey: 'entitlements.feature.custom_roles' },
  'feature.integrations': { labelKey: 'entitlements.feature.integrations' },
  'feature.reputation': {
    labelKey: 'entitlements.feature.reputation',
  },
  'feature.reputation_ai': { labelKey: 'entitlements.feature.reputation_ai' },
  'feature.advanced_analytics': {
    labelKey: 'entitlements.feature.advanced_analytics',
  },
  'feature.competitor_benchmark': {
    labelKey: 'entitlements.feature.competitor_benchmark',
  },
  'feature.lead_events': { labelKey: 'entitlements.feature.lead_events' },
  'feature.feedback': { labelKey: 'entitlements.feature.feedback' },
  'feature.profile_branding': {
    labelKey: 'entitlements.feature.profile_branding',
  },
  'feature.sso': { labelKey: 'entitlements.feature.sso' },
};

export const QUOTA_METADATA: Record<
  QuotaKey,
  { labelKey: string; unit: 'count' | 'seats' | 'locations' | 'days' }
> = {
  'quota.included_reputation_locations': {
    labelKey: 'entitlements.quota.included_reputation_locations',
    unit: 'locations',
  },
  'quota.members': {
    labelKey: 'entitlements.quota.members',
    unit: 'seats',
  },
  'quota.feedback_invite_recipients': {
    labelKey: 'entitlements.quota.feedback_invite_recipients',
    unit: 'count',
  },
  'quota.analytics_history_days': {
    labelKey: 'entitlements.quota.analytics_history_days',
    unit: 'days',
  },
};

/** Lowest plan that unlocks each feature (for upgrade CTAs). */
export const FEATURE_MINIMUM_PLAN: Record<FeatureKey, WorkspacePlan> = {
  'feature.invite_members': WorkspacePlan.TEAMS,
  'feature.custom_roles': WorkspacePlan.TEAMS,
  'feature.integrations': WorkspacePlan.PLUS,
  'feature.reputation': WorkspacePlan.PLUS,
  'feature.reputation_ai': WorkspacePlan.TEAMS,
  'feature.advanced_analytics': WorkspacePlan.PLUS,
  'feature.competitor_benchmark': WorkspacePlan.TEAMS,
  'feature.lead_events': WorkspacePlan.TEAMS,
  'feature.feedback': WorkspacePlan.PLUS,
  'feature.profile_branding': WorkspacePlan.PLUS,
  'feature.sso': WorkspacePlan.ENTERPRISE,
};

/** Plans from smallest to largest (comparison table column order). */
export const PLAN_ORDER = [
  WorkspacePlan.FREE,
  WorkspacePlan.PLUS,
  WorkspacePlan.TEAMS,
  WorkspacePlan.ENTERPRISE,
] as const;

/** Whether `plan` includes `feature`, i.e. is at or above its minimum plan. */
export function planIncludesFeature(
  plan: WorkspacePlan,
  feature: FeatureKey,
): boolean {
  return (
    PLAN_ORDER.indexOf(plan) >=
    PLAN_ORDER.indexOf(FEATURE_MINIMUM_PLAN[feature])
  );
}

export const PLAN_LABEL_KEYS: Record<WorkspacePlan, string> = {
  [WorkspacePlan.FREE]: 'entitlements.plan.free',
  [WorkspacePlan.PLUS]: 'entitlements.plan.plus',
  [WorkspacePlan.TEAMS]: 'entitlements.plan.teams',
  [WorkspacePlan.ENTERPRISE]: 'entitlements.plan.enterprise',
};
